import { ClientError } from "graphql-request"

import {
  AssociateUserActivityIdentityDocument,
  RegisterUserActivityIdentityDocument,
  RevokeUserActivityIdentityDocument,
  TrackUserActivityDocument,
  type TrackUserActivityMutationVariables,
} from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { waitForAmplitudeDeviceId } from "./amplitude"

// WERZ delivery of member activity (see docs/product/events/event-funnel-tracking.md). Each browser
// epoch has a visitor identity: a public UUID plus a private token only WERZ sees. Captured events
// wait in a persistent queue until the API accepts or rejects them, so a failed request is retried
// on the next capture, page load or reconnect.

const identityKey = "werz:activity-identity:v1"
const queueKey = "werz:activity-queue:v1"
// Identities replaced while logged in; revoked once none of their events are still queued.
const retiredKey = "werz:activity-retired:v1"
const MAX_QUEUED = 200

interface Identity {
  id: string
  token: string
  registered: boolean
  // The account this epoch is associated with. A different viewer, including none, starts a new
  // epoch: the API rejects pre-login capture after association and never reassigns an epoch.
  linkedUserId: string | null
}

type QueuedActivity = TrackUserActivityMutationVariables & {
  envelope: {
    occurrenceId: string
    anonymousId: string
    identityToken: string
    capturedUserId: string | null
    capturedAt: string
  }
}

interface Viewer {
  id: string
  token: string
}

let delivering = false
let runAgain = false
let started = false

function read<T>(key: string, json = true): T | null {
  try {
    const value = localStorage.getItem(key)
    return (json ? JSON.parse(value ?? "null") : value) as T | null
  } catch {
    return null
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* Storage can be unavailable; capture continues in Amplitude only. */
  }
}

// Read from storage rather than React state, so capture during auth hydration still knows the
// viewer and delivery sends the token that matches it.
function readViewer(): Viewer | null {
  const token = read<string>("accessToken", false)
  const id = read<{ id?: unknown }>("user")?.id
  if (!token || token === "null" || token === "undefined" || typeof id !== "string") return null
  return { id, token }
}

function newIdentity(): Identity {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  const token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
  const identity = { id: crypto.randomUUID(), token, registered: false, linkedUserId: null }
  write(identityKey, identity)
  return identity
}

const credentials = (identity: Identity) => ({
  anonymousId: identity.id,
  identityToken: identity.token,
})

// Sensitive requests pick their Authorization explicitly instead of inheriting the client header.
const request = (document: string, variables: Record<string, unknown>, authorization = "") =>
  graphQLClient.request(document, variables, { Authorization: authorization })

// The API answered and refused the request, so retrying it cannot succeed.
const isRefused = (error: unknown, codes: string[]) =>
  error instanceof ClientError &&
  (error.response.errors ?? []).some((graphqlError) =>
    codes.includes(String(graphqlError.extensions?.code)),
  )

// Revocation stops anyone holding a copy of the old token from adding events to that account.
// Resolves to whether it is done; an identity the API refuses is already unusable.
const revoke = (identity: Identity) =>
  request(RevokeUserActivityIdentityDocument, credentials(identity)).then(
    () => true,
    (error) => isRefused(error, ["BAD_USER_INPUT"]),
  )

// Registration is idempotent and refuses a revoked identity, so it asks whether the API still
// accepts this one. Only a refusal counts; a network failure does not.
const isRevoked = (identity: Identity) =>
  request(RegisterUserActivityIdentityDocument, credentials(identity)).then(
    () => false,
    (error) => isRefused(error, ["BAD_USER_INPUT"]),
  )

function currentIdentity(viewerId: string | null): Identity {
  const stored = read<Identity>(identityKey)
  if (stored && (!stored.linkedUserId || stored.linkedUserId === viewerId)) return stored
  // Revoking now would refuse its events still in the queue; deliver revokes it after them.
  if (stored?.registered) write(retiredKey, [...(read<Identity[]>(retiredKey) ?? []), stored])
  return newIdentity()
}

// Called on logout, before storage (and with it the queue) is cleared. Best effort: the page
// navigates right after.
export function retireActivityIdentity() {
  for (const identity of [read<Identity>(identityKey), ...(read<Identity[]>(retiredKey) ?? [])]) {
    if (identity?.registered) void revoke(identity)
  }
}

async function prepareIdentity(identity: Identity, viewer: Viewer | null) {
  if (!identity.registered) {
    await request(RegisterUserActivityIdentityDocument, credentials(identity))
    identity.registered = true
    write(identityKey, identity)
  }
  if (viewer && !identity.linkedUserId) {
    await request(AssociateUserActivityIdentityDocument, credentials(identity), viewer.token)
    identity.linkedUserId = viewer.id
    write(identityKey, identity)
  }
}

// Other identities with queued events: a second tab can replace a first-visit identity before it
// registers, and a replaced identity can still have events waiting. Registration and association
// are idempotent. A refused identity is skipped: its events are refused and dropped when sent.
async function prepareQueuedIdentities(
  queue: QueuedActivity[],
  currentId: string,
  viewer: Viewer | null,
) {
  const tokens = new Map(
    queue.map(({ envelope }) => [envelope.anonymousId, envelope.identityToken] as const),
  )
  tokens.delete(currentId)
  for (const [anonymousId, identityToken] of Array.from(tokens)) {
    const identity = { anonymousId, identityToken }
    const capturedByViewer = queue.some(
      ({ envelope }) =>
        envelope.anonymousId === anonymousId && envelope.capturedUserId === viewer?.id,
    )
    try {
      await request(RegisterUserActivityIdentityDocument, identity)
      if (viewer && capturedByViewer) {
        await request(AssociateUserActivityIdentityDocument, identity, viewer.token)
      }
    } catch (error) {
      if (!isRefused(error, ["BAD_USER_INPUT"])) throw error
    }
  }
}

// Revokes replaced identities whose events are all delivered. A failed revocation stays on the
// list for the next run.
async function revokeDrainedIdentities() {
  const retired = read<Identity[]>(retiredKey) ?? []
  if (!retired.length) return
  const queued = new Set(
    (read<QueuedActivity[]>(queueKey) ?? []).map(({ envelope }) => envelope.anonymousId),
  )
  const revoked = new Set<string>()
  for (const identity of retired) {
    if (!queued.has(identity.id) && (await revoke(identity))) revoked.add(identity.id)
  }
  // Re-read: a capture can have retired another identity meanwhile.
  write(
    retiredKey,
    (read<Identity[]>(retiredKey) ?? []).filter(({ id }) => !revoked.has(id)),
  )
}

async function deliver() {
  const queue = read<QueuedActivity[]>(queueKey) ?? []
  // Visitors who never capture anything never register an identity.
  if (!queue.length) return revokeDrainedIdentities()
  const viewer = readViewer()
  const current = currentIdentity(viewer?.id ?? null)
  try {
    await prepareIdentity(current, viewer)
  } catch (error) {
    // A refused identity cannot recover; the next capture starts a new one.
    if (isRefused(error, ["BAD_USER_INPUT"])) localStorage.removeItem(identityKey)
    return
  }
  try {
    await prepareQueuedIdentities(queue, current.id, viewer)
  } catch {
    return
  }
  // Events captured before Amplitude started lack the bridge key. They are already queued, so
  // waiting here cannot lose them; once Amplitude has started this returns at once.
  const amplitudeDeviceId = await waitForAmplitudeDeviceId()

  // Oldest first, re-reading the queue so events captured meanwhile go out in the same run. The
  // seen set stops the run if a failed storage write left a delivered item in place.
  const seen = new Set<string>()
  let checkedCurrent = false
  for (
    let item = read<QueuedActivity[]>(queueKey)?.[0];
    item && !seen.has(item.envelope.occurrenceId);
    item = read<QueuedActivity[]>(queueKey)?.[0]
  ) {
    const { capturedUserId, occurrenceId } = item.envelope
    seen.add(occurrenceId)
    // Captured after a login during this run: a new run first links the identity to that account.
    if (capturedUserId && capturedUserId !== viewer?.id && capturedUserId === readViewer()?.id) {
      runAgain = true
      return
    }
    try {
      // Authenticated capture is only accepted from its own account; another account's is dropped.
      if (!capturedUserId || capturedUserId === viewer?.id) {
        const data = {
          ...item.data,
          amplitudeDeviceId: item.data?.amplitudeDeviceId ?? amplitudeDeviceId,
        }
        await request(
          TrackUserActivityDocument,
          { ...item, data },
          capturedUserId ? viewer?.token : "",
        )
      }
    } catch (error) {
      if (!isRefused(error, ["BAD_USER_INPUT", "GRAPHQL_VALIDATION_FAILED", "UNAUTHENTICATED"])) {
        return
      }
      // A registered, linked identity makes no identity request, so a revocation by the API only
      // shows up as refused events. Keeping it would drop every later event; the next capture
      // starts a new one. Checked once per run.
      if (
        !checkedCurrent &&
        item.envelope.anonymousId === current.id &&
        isRefused(error, ["BAD_USER_INPUT"])
      ) {
        checkedCurrent = true
        if ((await isRevoked(current)) && read<Identity>(identityKey)?.id === current.id) {
          localStorage.removeItem(identityKey)
        }
      }
    }
    write(
      queueKey,
      (read<QueuedActivity[]>(queueKey) ?? []).filter(
        (queued) => queued.envelope.occurrenceId !== occurrenceId,
      ),
    )
  }
  await revokeDrainedIdentities()
}

// One delivery run at a time. A call during a run asks for one more run after it, so its event
// never waits for the next trigger.
async function deliverActivity() {
  runAgain = true
  if (delivering) return
  delivering = true
  try {
    while (runAgain) {
      runAgain = false
      await deliver()
    }
  } finally {
    delivering = false
  }
}

export function startActivityDelivery() {
  if (started || typeof window === "undefined") return
  started = true
  window.addEventListener("online", () => void deliverActivity())
  void deliverActivity()
}

export function captureActivity(
  activity: Omit<TrackUserActivityMutationVariables, "envelope">,
  occurrence: { occurrenceId: string; capturedAt: string },
) {
  const viewer = readViewer()
  const identity = currentIdentity(viewer?.id ?? null)
  const item: QueuedActivity = {
    ...activity,
    envelope: {
      ...occurrence,
      ...credentials(identity),
      capturedUserId: viewer?.id ?? null,
    },
  }
  write(queueKey, [...(read<QueuedActivity[]>(queueKey) ?? []), item].slice(-MAX_QUEUED))
  void deliverActivity()
}
